import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { withToastManager } from 'react-toast-notifications';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { nsOptions } from '../i18n';
import { twoFactorActions, authActions } from '../redux/actions';
import LabeledInput from './LabeledInput';
import ErrorUtil from '../utils/ErrorUtil';
import Toast from '../utils/Toast';
import { setLanguage } from '../utils/language';
import history from '../history';
import '../assets/css/two-factor.css';

const mapDispatchToProps = (dispatch) => ({
  verify2FA: async (token, tempToken, trustDevice, deviceName) => dispatch(
    twoFactorActions.verify(token, tempToken, trustDevice, deviceName),
  ),
  authSuccess: (user) => dispatch(authActions.authSuccess(user)),
});

const TEMP_TOKEN_EXPIRY = 5 * 60 * 1000; // 5 minutes

@withToastManager
@connect(null, mapDispatchToProps)
@withTranslation('', nsOptions)
class TwoFactorLogin extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    i18n: PropTypes.shape().isRequired,
    tempToken: PropTypes.string.isRequired,
    verify2FA: PropTypes.func.isRequired,
    authSuccess: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
  };

  static defaultProps = {
    onCancel: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      isVerifying: false,
      useBackupCode: false,
      trustDevice: false,
      timeRemaining: TEMP_TOKEN_EXPIRY / 1000, // in seconds
      error: null,
    };

    this.tokenInput = null;
    this.startTime = Date.now();
  }

  componentDidMount() {
    // Start countdown timer
    this.timerInterval = setInterval(() => {
      const elapsed = Date.now() - this.startTime;
      const remaining = Math.max(0, (TEMP_TOKEN_EXPIRY - elapsed) / 1000);
      this.setState({ timeRemaining: remaining });

      if (remaining === 0) {
        clearInterval(this.timerInterval);
        this.handleExpired();
      }
    }, 1000);
  }

  componentWillUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  handleExpired = () => {
    Toast.error(this.props, 'twoFactor:login.expired');
    sessionStorage.removeItem('2fa-temp-token');
    if (this.props.onCancel) {
      this.props.onCancel();
    } else {
      history.push('/auth/login');
    }
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    const {
      verify2FA, tempToken, authSuccess, i18n, t,
    } = this.props;

    if (!this.tokenInput || !this.tokenInput.value) {
      return;
    }

    const token = this.tokenInput.value.replace(/\s/g, ''); // Remove spaces

    // Validate token format
    const { useBackupCode } = this.state;
    if (useBackupCode) {
      if (!/^[A-F0-9]{8}$/i.test(token)) {
        this.setState({ error: t('twoFactor:login.error-invalid') });
        return;
      }
    } else if (!/^\d{6}$/.test(token)) {
      this.setState({ error: t('twoFactor:login.error-invalid') });
      return;
    }

    this.setState({ isVerifying: true, error: null });

    try {
      const { trustDevice } = this.state;
      const user = await verify2FA(token, tempToken, trustDevice, '');
      authSuccess(user);
      await setLanguage(i18n, user.language);
      Toast.success(this.props, 'error:valid.connected', 2000);
      history.push('/');
    } catch (error) {
      this.setState({ isVerifying: false });
      ErrorUtil.handleCatched(this.props, error);
      // Clear input on error
      if (this.tokenInput) {
        this.tokenInput.value = '';
      }
    }
  };

  toggleBackupCode = () => {
    this.setState((prevState) => ({
      useBackupCode: !prevState.useBackupCode,
      error: null,
    }));
    // Clear input when toggling
    if (this.tokenInput) {
      this.tokenInput.value = '';
    }
  };

  formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  getTimerClass = () => {
    const { timeRemaining } = this.state;
    if (timeRemaining < 60) return 'two-factor-timer danger';
    if (timeRemaining < 120) return 'two-factor-timer warning';
    return 'two-factor-timer';
  };

  onKeyPress = (event) => {
    if (event.key === 'Enter') this.handleSubmit(event);
  };

  render() {
    const { t } = this.props;
    const {
      isVerifying, useBackupCode, timeRemaining, error, trustDevice,
    } = this.state;

    return (
      <div className="two-factor-login">
        <div className="two-factor-login-header">
          <div className="two-factor-login-icon">
            <FontAwesomeIcon icon={['fal', 'lock']} />
          </div>
          <h2>{t('twoFactor:login.title')}</h2>
          {useBackupCode ? (
            <p className="text-muted">
              {t('twoFactor:backup-codes.description')}
            </p>
          ) : (
            <p className="font-weight-bold" style={{ color: '#33DFB4' }}>
              {t('twoFactor:login.description')}
            </p>
          )}
        </div>

        <form onSubmit={this.handleSubmit}>
          <LabeledInput
            type="text"
            name="token"
            label=""
            placeholder={useBackupCode ? t('twoFactor:login.backup-code-placeholder') : t('twoFactor:login.placeholder')}
            required
            onValidChange={(e) => {
              this.tokenInput = e.target;
            }}
            onKeyPress={this.onKeyPress}
            autoFocus
            inputClassName={`labeled-input ${error ? 'invalid' : ''}`}
            autoComplete="off"
          />

          {error && (
            <div className="alert alert-danger mt-2" role="alert">
              {error}
            </div>
          )}

          <div className={this.getTimerClass()}>
            <FontAwesomeIcon icon={['fal', 'clock']} className="mr-2" />
            {t('twoFactor:login.time-remaining')}
            {' '}
            {this.formatTime(timeRemaining)}
          </div>

          <div className="custom-control custom-checkbox mt-3 mb-2">
            <input
              type="checkbox"
              className="custom-control-input"
              id="trustDeviceCheckbox"
              checked={trustDevice}
              onChange={(e) => this.setState({ trustDevice: e.target.checked })}
            />
            <label className="custom-control-label" htmlFor="trustDeviceCheckbox">
              <small>
                {t('twoFactor:login.trust-device')}
              </small>
            </label>
          </div>

          <button
            type="submit"
            className="btn btn-newturquoise-1 btn-block"
            disabled={isVerifying}
          >
            {isVerifying ? (
              <>
                <FontAwesomeIcon icon={['fal', 'spinner']} spin className="mr-2" />
                {t('twoFactor:login.verifying')}
              </>
            ) : (
              t('twoFactor:login.verify-button')
            )}
          </button>
        </form>

        <div className="two-factor-toggle">
          <button type="button" onClick={this.toggleBackupCode}>
            {useBackupCode
              ? t('twoFactor:login.back-to-code')
              : t('twoFactor:login.use-backup-code')}
          </button>
        </div>
      </div>
    );
  }
}

export default TwoFactorLogin;
